import React from "react";
import styled from "styled-components";
import UpsolveLogo from "../../../static/images/upsolve-logo.svg";
import SoloLogo from "../../../static/images/solo-logo.svg";
import CloseButtonIcon from "../../../static/images/close-button.svg";
import { HR, theme } from "@upsolve/ui";
import trackComponentAction from "../analytics/trackComponentAction";
import { CTATarget, getCTATrackingTarget, SoloReferralLink } from "./getCTAData";
import getMyUpsolveEntryUrl from "../Links/getMyUpsolveEntryUrl";
import usePortal from "react-useportal";

type SoloModalProps = {
  onHide: () => void;
  trackingPageContext: any;
};

const UpsolveLogoStyled = styled(UpsolveLogo)`
  height: 32px;
  width: auto;
`;

export const SoloModal = (props: SoloModalProps) => {
  const { Portal } = usePortal();

  const onClickBankruptcyCTA = () => {
    trackComponentAction({
      componentName: "soloModal",
      actionId: getCTATrackingTarget(CTATarget.SCREENER),
      actionMethod: "click",
      pageContext: props.trackingPageContext,
    });
    window.open(getMyUpsolveEntryUrl());
    props.onHide();
  };

  const onClickSoloCTA = () => {
    trackComponentAction({
      componentName: "soloModal",
      actionId: "CTA.solo",
      actionMethod: "click",
      pageContext: props.trackingPageContext,
    });
    window.open(SoloReferralLink);
    props.onHide();
  };

  return (
    <Portal>
      <StyledSoloModalContents onClick={props.onHide}>
        <div onClick={(e) => e.stopPropagation()} className="modal-content">
          <CloseButton onClick={props.onHide}>
            <CloseButtonIcon />
          </CloseButton>
          <div className="option">
            <h2>
              <UpsolveLogoStyled />
            </h2>
            <h3>Stop a Debt Lawsuit with Bankruptcy</h3>
            <p>
              Chapter 7 bankruptcy erases many debts, including lawsuit judgments. If you're facing a debt lawsuit,
              bankruptcy eliminates most debts and gives you a fresh start. Upsolve can help you file for free.
            </p>
            <div className="criteria-container">
              <div className="criteria">
                <h4>Who is this good for?</h4>
                <p>
                  Debt balance <strong>above</strong> $10,000
                </p>
                <p>
                  You <strong>rent</strong> your home
                </p>
              </div>
            </div>
            <button className="cta-button" onClick={onClickBankruptcyCTA}>
              Explore Bankruptcy
            </button>
          </div>
          <HR className="divider" />
          <div className="option">
            <h2>
              <SoloLogo />
            </h2>
            <h3>Respond to a Lawsuit & Negotiate a Settlement</h3>
            <p>
              If you're sued for debt, you must respond—even if you plan to file for bankruptcy later. For a reasonable
              fee, Solo can help you respond to your lawsuit and, if you want, negotiate a settlement.
            </p>
            <div className="criteria-container">
              <div className="criteria">
                <h4>Who is this good for?</h4>
                <p>
                  Debt balance <strong>below</strong> $10,000
                </p>
                <p>
                  You <strong>own</strong> your home
                </p>
                <p>
                  <strong>Haven't</strong> responded to a lawsuit
                </p>
              </div>
            </div>
            <button className="cta-button" onClick={onClickSoloCTA}>
              Visit SoloSuit
            </button>
          </div>
        </div>
      </StyledSoloModalContents>
    </Portal>
  );
};

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledSoloModalContents = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: 10000;
  padding: 16px;

  .divider {
    display: none;
    margin: 0;
    @media (max-width: ${theme.breakpoints[500]}) {
      display: block;
    }
  }

  .modal-content {
    display: flex;
    background-color: white;
    padding: 40px;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    width: 680px;
    height: 609px;
    gap: 40px;
    position: relative;
    @media (max-width: ${theme.breakpoints[500]}) {
      flex-direction: column;
      width: 408px;
      height: 100%;
      overflow-y: auto;
    }
  }

  .option {
    flex: 1;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  h2 {
    color: #3c5dff;
    height: 30px;
    width: auto;
    margin: 0;
  }

  h3 {
    font-size: 18px;
    margin: 0;
  }

  p {
    font-size: 16px;
    color: #555;
    margin: 0;
  }

  .criteria-container {
    display: flex;
    justify-content: center;
    flex: 1;
    width: 100%;
  }

  .criteria {
    width: 265px;
    background-color: #f5f5f5;
    padding: 16px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: fit-content;
    h4 {
      font-size: 12px;
      color: #888;
      text-transform: uppercase;
      margin: 0;
    }
    p {
      line-height: 27px;
    }
  }

  .cta-button {
    background-color: #3c5dff;
    color: white;
    padding: 10px 24px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 24px;
    font-weight: 700;
  }

  .cta-button:hover {
    background-color: #2a4bcc;
  }
`;
